import React, { useState, useEffect, useRef } from "react";
import {
  ModelCost,
  CostItem,
  Workshop,
  Model,
  Firm,
  CalculationType,
} from "../types";
import {
  costService,
  modelService,
  firmService,
  workshopService,
} from "../services/dataService";
import { formatCurrency, formatNumber } from "../utils/formatters";
import { useExchangeRates } from "../context/ExchangeRateContext";
import AlertModal from "./AlertModal";
import ConfirmModal from "./ConfirmModal";
import "../pages/PurchaseOrders.css";
import "./OrderCostsModal.css";

/**
 * Sipariş maliyetleri penceresi — Siparişler (satır menüsü → Sipariş maliyetleri) ve Mali Raporlar'dan açılır.
 * Ortak po-* dili (PurchaseOrders.css) + yalnız bu pencereye özgü oc-* ekleri (OrderCostsModal.css).
 * Görünüm Model Maliyetleri ekranındaki set kartıyla aynı: başlıkta Kalem / Birim / Toplam rakamları, altında kalem tablosu.
 * Esc sırası: onay penceresi → alt form (ekle/düzenle) → bu pencere; kayıt sürerken kapanmaz.
 */

interface OrderCostsModalProps {
  isOpen: boolean;
  orderId: string;
  modelId: string;
  onClose: () => void;
}

const I = {
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>,
  cal: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>,
  rate: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>,
  coins: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="9" cy="7" rx="6" ry="3" /><path d="M3 7v5c0 1.7 2.7 3 6 3s6-1.3 6-3V7" /><path d="M9 15v2c0 1.7 2.7 3 6 3s6-1.3 6-3v-5c0-1.7-2.7-3-6-3" /></svg>,
};

const OrderCostsModal: React.FC<OrderCostsModalProps> = ({
  isOpen,
  orderId,
  modelId,
  onClose,
}) => {
  const { usdRate, eurRate, gbpRate } = useExchangeRates();

  // State
  const [model, setModel] = useState<Model | null>(null);
  const [firm, setFirm] = useState<Firm | null>(null);
  const [modelCosts, setModelCosts] = useState<ModelCost[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false); // ekle/güncelle/kaldır sürerken pencereler kapanmaz
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState<ModelCost | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [costItems, setCostItems] = useState<CostItem[]>([]);
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Alert Modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  // Confirm Modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "danger";
    onConfirm: () => void;
  }>({ isOpen: false, message: "", type: "warning", onConfirm: () => {} });

  useEffect(() => {
    if (isOpen && orderId && modelId) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, orderId, modelId]);

  // Esc: önce en üstteki katman kapanır (onay → alt form → bu pencere). Yakalama aşaması + stopPropagation:
  // alttaki sayfanın (ör. Mali Raporlar detay penceresi) Esc dinleyicisine ulaşmaz.
  const escRef = useRef<() => void>(() => {});
  escRef.current = () => {
    if (confirmModal.isOpen) {
      setConfirmModal((c) => ({ ...c, isOpen: false }));
      return;
    }
    if (saving) return;
    if (showAddModal) {
      setShowAddModal(false);
      return;
    }
    if (showEditModal) {
      setShowEditModal(false);
      setEditingItem(null);
      return;
    }
    onClose();
  };
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      escRef.current();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [isOpen]);

  // Açılınca odak pencereye; kapanınca açan öğeye geri döner (hâlâ sayfadaysa)
  useEffect(() => {
    if (!isOpen) return;
    const opener = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus({ preventScroll: true });
    return () => {
      if (opener && opener.isConnected) opener.focus?.({ preventScroll: true });
    };
  }, [isOpen]);

  const loadData = async () => {
    try {
      setLoading(true);

      const [allModels, allFirms, costs, items, workshopList] =
        await Promise.all([
          modelService.getAll(),
          firmService.getFirms(),
          costService.getModelCosts(orderId),
          costService.getCostItems(),
          workshopService.getAll(),
        ]);

      const foundModel = allModels.find((m) => m.modelId === modelId);
      setModel(foundModel || null);

      if (foundModel?.firmId) {
        const foundFirm = allFirms.find((f) => f.firmId === foundModel.firmId);
        setFirm(foundFirm || null);
      }

      setModelCosts(costs);
      setCostItems(items);
      setWorkshops(workshopList);
    } catch (error) {
      console.error("Failed to load order costs:", error);
      setAlertModal({
        isOpen: true,
        message: "Maliyet verileri yüklenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Döviz dönüşümü fonksiyonu
  const convertToTRY = (
    amount: number,
    currency: string,
    exchangeRates: { usdRate?: number; eurRate?: number; gbpRate?: number },
  ): number => {
    if (currency === "TRY") return amount;

    const { usdRate, eurRate, gbpRate } = exchangeRates;

    switch (currency) {
      case "USD":
        if (!usdRate) return 0;
        return amount * usdRate;
      case "EUR":
        if (!eurRate) return 0;
        return amount * eurRate;
      case "GBP":
        if (!gbpRate) return 0;
        return amount * gbpRate;
      default:
        return amount;
    }
  };

  // Toplam maliyeti TL'ye çevirerek hesapla
  const calculateTotalCost = (): number => {
    return modelCosts.reduce((sum, item) => {
      const itemTotal = item.totalCost || 0;
      const tryAmount = convertToTRY(itemTotal, item.currency || "TRY", {
        usdRate: item.usdRate,
        eurRate: item.eurRate,
        gbpRate: item.gbpRate,
      });
      return sum + tryAmount;
    }, 0);
  };

  const handleDelete = async (item: ModelCost) => {
    setConfirmModal({
      isOpen: true,
      message: `"${item.costItemName || "Bu"}" maliyet kalemini kaldırmak istediğinizden emin misiniz?`,
      type: "danger",
      onConfirm: async () => {
        try {
          setLoading(true);
          setSaving(true);
          await costService.deleteModelCost(item.modelCostId);
          setAlertModal({
            isOpen: true,
            message: "Maliyet başarıyla kaldırıldı!",
            type: "success",
          });
          loadData();
        } catch (error) {
          console.error("Failed to delete model cost:", error);
          setAlertModal({
            isOpen: true,
            message: "Maliyet kaldırılamadı!",
            type: "error",
          });
        } finally {
          setLoading(false);
          setSaving(false);
        }
      },
    });
  };

  const handleEdit = (item: ModelCost) => {
    const costItem = costItems.find((ci) => ci.costItemId === item.costItemId);
    const enrichedItem = {
      ...item,
      quantity1Label: item.quantity1Label || costItem?.quantity1Label,
      quantity2Label: item.quantity2Label || costItem?.quantity2Label,
      calculationType: item.calculationType || costItem?.calculationType,
    };
    setEditingItem(enrichedItem);
    setShowEditModal(true);
  };

  const handleUpdateModelCost = async (updatedItem: ModelCost) => {
    try {
      setLoading(true);
      setSaving(true);

      // CalculationType CustomCost (6/Fason) ise quantity 0 gönder, totalCost kullanılır
      const updateData = { ...updatedItem };
      if (updatedItem.calculationType === CalculationType.CustomCost) {
        updateData.quantity = 0;
      }

      await costService.updateModelCost(updatedItem.modelCostId, updateData);
      setAlertModal({
        isOpen: true,
        message: "Maliyet başarıyla güncellendi!",
        type: "success",
      });
      setShowEditModal(false);
      setEditingItem(null);
      loadData();
    } catch (error) {
      console.error("Failed to update model cost:", error);
      setAlertModal({
        isOpen: true,
        message: "Maliyet güncellenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
      setSaving(false);
    }
  };

  const handleAddModelCost = async (newCost: Partial<ModelCost>) => {
    try {
      setLoading(true);
      setSaving(true);

      // CalculationType CustomCost için quantity 0 gönder, totalCost kullanılır
      const modelCostData = {
        ...newCost,
        modelId,
        orderId,
        usdRate: usdRate || undefined,
        eurRate: eurRate || undefined,
        gbpRate: gbpRate || undefined,
        exchangeRateDate: new Date().toISOString(),
        isActive: true,
      };

      // CalculationType CustomCost (6/Fason) ise quantity 0 olarak gönder
      if (newCost.calculationType === CalculationType.CustomCost) {
        modelCostData.quantity = 0;
      }

      await costService.addModelCost(
        modelCostData as Omit<ModelCost, "modelCostId">,
      );
      setAlertModal({
        isOpen: true,
        message: "Maliyet başarıyla eklendi!",
        type: "success",
      });
      setShowAddModal(false);
      loadData();
    } catch (error: any) {
      console.error("Failed to add model cost:", error);
      setAlertModal({
        isOpen: true,
        message: "Maliyet eklenemedi: " + (error.message || "Bilinmeyen hata"),
        type: "error",
      });
    } finally {
      setLoading(false);
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const firstCost = modelCosts[0];
  const orderQuantity = firstCost?.orderQuantity || 0;
  const totalCost = calculateTotalCost();
  const unitCost = orderQuantity > 0 ? totalCost / orderQuantity : 0;
  const modelText = model ? model.modelCode || model.modelName : "";
  const kicker = [firm?.firmName, modelText ? `Model ${modelText}` : ""].filter(Boolean).join(" · ") || "Sipariş";

  return (
    <>
      <div
        className="po-overlay center top"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget && !saving) onClose();
        }}
      >
        <div
          className="po-modal xl oc-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="oc-title"
          tabIndex={-1}
          ref={dialogRef}
        >
          <div className="po-modal-head">
            <div className="oc-head-text">
              <span className="po-kicker" title={kicker}>{kicker}</span>
              <h3 id="oc-title">Sipariş maliyetleri</h3>
            </div>
            <button type="button" className="po-ib big" aria-label="Kapat" onClick={onClose} disabled={saving}>{I.close}</button>
          </div>

          <div className="po-modal-body">
            {loading && modelCosts.length === 0 ? (
              <div className="po-loading"><span className="po-spin" /> Maliyetler yükleniyor</div>
            ) : (
              <section className={`po-card oc-set ${loading ? "dim" : ""}`} aria-busy={loading}>
                {/* Set başlığı — Model Maliyetleri ekranındaki kartla aynı düzen */}
                <div className="oc-set-head">
                  <div className="oc-set-main">
                    <div className="oc-set-title">
                      <b>Sipariş</b>
                      <span className="po-codepill" title={orderId}>#{orderId.slice(0, 8).toUpperCase()}</span>
                      {orderQuantity > 0 && <span className="po-badge info">{orderQuantity} adet</span>}
                    </div>
                    {firstCost && (
                      <div className="oc-set-meta">
                        {firstCost.exchangeRateDate && (
                          <span>{I.cal}Kur tarihi <b>{new Date(firstCost.exchangeRateDate).toLocaleDateString("tr-TR")}</b></span>
                        )}
                        <span>
                          {I.rate}
                          USD <b>{firstCost.usdRate?.toFixed(4) || "yok"}</b>
                          {" · "}EUR <b>{firstCost.eurRate?.toFixed(4) || "yok"}</b>
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="oc-set-figs">
                    <div><span>Kalem</span><b>{modelCosts.length}</b></div>
                    {orderQuantity > 0 && <div><span>Birim</span><b>{formatCurrency(unitCost, "TRY")}</b></div>}
                    <div className="grand"><span>Toplam</span><b>{formatCurrency(totalCost, "TRY")}</b></div>
                  </div>
                  <button type="button" className="po-btn small primary oc-set-add" onClick={() => setShowAddModal(true)} disabled={saving}>
                    {I.plus} Kalem ekle
                  </button>
                </div>

                {modelCosts.length === 0 ? (
                  <div className="po-blank">
                    <span className="po-blank-icon">{I.coins}</span>
                    <b>Bu siparişte maliyet kalemi yok</b>
                    Atölyenin tanımlı kalemlerinden seçerek ilk maliyeti ekleyin.
                  </div>
                ) : (
                  <div className="po-table-wrap">
                    <table className="po-table small oc-table">
                      <thead>
                        <tr>
                          <th>Maliyet kalemi</th>
                          <th className="num">Miktar 1</th>
                          <th className="num">Miktar 2</th>
                          <th className="num">Fire</th>
                          <th className="num">Net miktar</th>
                          <th className="num">Birim fiyat</th>
                          <th className="num">Toplam</th>
                          <th className="num">TL karşılığı</th>
                          <th>Kullanım</th>
                          <th className="act" aria-label="İşlemler" />
                        </tr>
                      </thead>
                      <tbody>
                        {modelCosts.map((item) => {
                          const tryEquivalent = convertToTRY(
                            item.totalCost || 0,
                            item.currency || "TRY",
                            {
                              usdRate: item.usdRate,
                              eurRate: item.eurRate,
                              gbpRate: item.gbpRate,
                            },
                          );

                          return (
                            <tr key={item.modelCostId} className="static">
                              <td>
                                <div className="po-name">{item.costItemName || "—"}</div>
                                <div className="po-sub">{item.costCategoryName || "kategori yok"}</div>
                              </td>
                              <td className="num">
                                <b>{item.quantity}</b> <span className="po-sub">{item.unit || ""}</span>
                              </td>
                              <td className="num">
                                {item.quantity2 ? (
                                  <><b>{item.quantity2}</b> <span className="po-sub">{item.unit2 || ""}</span></>
                                ) : (
                                  <span className="oc-nil">—</span>
                                )}
                              </td>
                              <td className="num">
                                {item.wastagePercentage !== undefined && item.wastagePercentage !== null ? (
                                  <span className={item.wastagePercentage > 0 ? "oc-waste" : "po-sub"}>%{item.wastagePercentage}</span>
                                ) : (
                                  <span className="oc-nil">—</span>
                                )}
                              </td>
                              <td className="num">
                                {item.actualQuantityNeeded ? <b>{formatNumber(item.actualQuantityNeeded)}</b> : item.quantity}
                              </td>
                              <td className="num">
                                {item.unitPrice ? formatCurrency(item.unitPrice, item.currency) : <span className="oc-nil">—</span>}
                              </td>
                              <td className="num">
                                <span className="po-amt">{item.totalCost ? formatCurrency(item.totalCost, item.currency) : "—"}</span>
                              </td>
                              <td className="num">
                                <span className="po-amt oc-try">{formatCurrency(tryEquivalent, "TRY")}</span>
                                {item.currency !== "TRY" && item.usdRate && (
                                  <div className="po-sub">
                                    1 {item.currency} ={" "}
                                    {item.currency === "USD"
                                      ? item.usdRate
                                      : item.currency === "EUR"
                                        ? item.eurRate
                                        : item.gbpRate}{" "}
                                    TL
                                  </div>
                                )}
                              </td>
                              <td>
                                {item.order?.workshop?.name ? (
                                  <span className="po-badge muted">{item.order.workshop.name}</span>
                                ) : item.usage ? (
                                  <span className="oc-usage" title={item.usage}>
                                    {item.usage.substring(0, 20)}
                                    {item.usage.length > 20 ? "…" : ""}
                                  </span>
                                ) : (
                                  <span className="oc-nil">—</span>
                                )}
                              </td>
                              <td className="act">
                                <div className="po-rowacts">
                                  <button type="button" className="po-ib" title="Düzenle" aria-label={`${item.costItemName || "Kalemi"} düzenle`} onClick={() => handleEdit(item)} disabled={saving}>{I.edit}</button>
                                  <button type="button" className="po-ib danger" title="Kaldır" aria-label={`${item.costItemName || "Kalemi"} kaldır`} onClick={() => handleDelete(item)} disabled={saving}>{I.trash}</button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}
          </div>

          <div className="po-modal-foot between">
            <span className="po-hint oc-foot-hint">Dövizli kalemler kayıt anındaki kurla TL'ye çevrilir; birim maliyet = toplam ÷ sipariş adedi.</span>
            <div className="po-foot-btns">
              <button type="button" className="po-btn" onClick={onClose} disabled={saving}>Kapat</button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && editingItem && (
        <EditCostModal
          item={editingItem}
          saving={saving}
          onClose={() => {
            setShowEditModal(false);
            setEditingItem(null);
          }}
          onSave={handleUpdateModelCost}
        />
      )}

      {/* Add Modal */}
      {showAddModal && (
        <AddCostModal
          saving={saving}
          onClose={() => setShowAddModal(false)}
          onSave={handleAddModelCost}
          costItems={costItems}
          workshops={workshops}
        />
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText="Kaldır"
        onConfirm={() => {
          confirmModal.onConfirm();
          setConfirmModal({ ...confirmModal, isOpen: false });
        }}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </>
  );
};

/** Alt form açılınca ilk alana odaklanır, kapanınca açan düğmeye döner (autoFocus yerine: StrictMode geri-odak temizliği ezmesin). */
const useSubFocus = <T extends HTMLElement>() => {
  const [opener] = useState(() => document.activeElement as HTMLElement | null);
  const firstRef = useRef<T | null>(null);
  useEffect(() => () => { if (opener && opener.isConnected) opener.focus?.({ preventScroll: true }); }, [opener]);
  useEffect(() => { firstRef.current?.focus({ preventScroll: true }); }, []);
  return firstRef;
};

// Edit Cost Modal Component
interface EditCostModalProps {
  item: ModelCost;
  saving?: boolean;
  onClose: () => void;
  onSave: (item: ModelCost) => void;
}

const EditCostModal: React.FC<EditCostModalProps> = ({
  item,
  saving,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<ModelCost>(item);
  const firstRef = useSubFocus<HTMLInputElement>();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (field: keyof ModelCost, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="po-overlay center top oc-sub" onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <form className="po-modal" role="dialog" aria-modal="true" aria-labelledby="oc-edit-title" onSubmit={handleSubmit}>
        <div className="po-modal-head">
          <div>
            <span className="po-kicker">Sipariş maliyeti</span>
            <h3 id="oc-edit-title">Miktarı düzenle</h3>
          </div>
          <button type="button" className="po-ib big" aria-label="Kapat" onClick={onClose} disabled={saving}>{I.close}</button>
        </div>
        <div className="po-modal-body">
          {/* Maliyet kalemi bilgisi — yalnız gösterim */}
          <dl className="po-facts">
            <div className="wide"><dt>Maliyet kalemi</dt><dd>{formData.costItemName || "—"}</dd></div>
            <div><dt>Kategori</dt><dd>{formData.costCategoryName || "—"}</dd></div>
            <div><dt>Birim fiyat</dt><dd>{formData.unitPrice} {formData.currency}</dd></div>
          </dl>

          <div className="po-grid2">
            <label className="po-field">
              <span>{formData.quantity1Label || "Miktar"} <em className="err">zorunlu</em></span>
              <input
                ref={firstRef}
                type="number"
                step="0.01"
                value={formData.quantity || ""}
                onChange={(e) => handleChange("quantity", parseFloat(e.target.value) || 0)}
                required
              />
            </label>
            <label className="po-field">
              <span>Birim</span>
              <input type="text" value={formData.unit || ""} readOnly disabled />
            </label>

            {/* Quantity 2 */}
            {formData.quantity2 !== undefined && formData.quantity2 !== null && (
              <>
                <label className="po-field">
                  <span>{formData.quantity2Label || "Miktar 2"}</span>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.quantity2 || ""}
                    onChange={(e) => handleChange("quantity2", parseFloat(e.target.value) || undefined)}
                  />
                </label>
                <label className="po-field">
                  <span>Birim 2</span>
                  <input type="text" value={formData.unit2 || ""} readOnly disabled />
                </label>
              </>
            )}
          </div>
        </div>
        <div className="po-modal-foot">
          <button type="button" className="po-btn" onClick={onClose} disabled={saving}>Vazgeç</button>
          <button type="submit" className="po-btn primary" disabled={saving}>{saving ? "Kaydediliyor…" : "Kaydet"}</button>
        </div>
      </form>
    </div>
  );
};

// Add Cost Modal Component
interface AddCostModalProps {
  saving?: boolean;
  onClose: () => void;
  onSave: (cost: Partial<ModelCost>) => void;
  costItems: CostItem[];
  workshops: Workshop[];
}

interface ModelCostFormData extends Partial<ModelCost> {
  quantity1Label?: string;
  quantity2Label?: string;
}

const AddCostModal: React.FC<AddCostModalProps> = ({
  saving,
  onClose,
  onSave,
  costItems,
  workshops,
}) => {
  const [formData, setFormData] = useState<ModelCostFormData>({
    costItemId: "",
    quantity: 0,
    unit: "",
    quantity2: undefined,
    unit2: "",
    unitPrice: 0,
    totalCost: 0,
    currency: "TRY",
    usage: "",
    notes: "",
    wastagePercentage: 0,
    quantity1Label: "",
    quantity2Label: "",
    calculationType: undefined,
  });

  const [, setSelectedCostItem] = useState<CostItem | null>(null);
  const [selectedWorkshopId, setSelectedWorkshopId] = useState<string>("");
  const [workshopCostItems, setWorkshopCostItems] = useState<CostItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [formError, setFormError] = useState("");
  const firstRef = useSubFocus<HTMLSelectElement>();

  const handleWorkshopChange = async (workshopId: string) => {
    setSelectedWorkshopId(workshopId);
    setFormError("");
    const selectedWorkshop = workshops.find((w) => w.workshopId === workshopId);

    setFormData((prev) => ({
      ...prev,
      usage: selectedWorkshop?.name || "",
      costItemId: "",
    }));

    if (workshopId) {
      try {
        setLoadingItems(true);
        const workshopItems =
          await costService.getWorkshopCostItems(workshopId);

        const items: CostItem[] = workshopItems
          .filter((wi) => wi.costItemId)
          .map((wi) => ({
            costItemId: wi.costItemId,
            costCategoryId: "",
            itemName: wi.costItemName || "",
            description: wi.costItemDescription,
            unit: wi.unitCode,
            unitName: wi.quantity1Label,
            unit2: wi.unitCode2,
            unitPrice: wi.effectivePrice || wi.standardPrice || 0,
            currency: wi.effectiveCurrency || wi.standardCurrency || "TRY",
            supplier: undefined,
            wastagePercentage: undefined,
            minimumOrderQuantity: undefined,
            discountPercentage: wi.workshopDiscountPercentage || undefined,
            lastPriceUpdate: new Date().toISOString(),
            isActive: true,
            calculationType: wi.calculationType,
            materialWidth: wi.materialWidth,
            materialHeight: wi.materialHeight,
            quantity1Label: wi.quantity1Label,
            quantity2Label: wi.quantity2Label,
            quantity3Label: wi.quantity3Label,
            costCategory: {
              costCategoryId: "",
              category: wi.categoryName || "",
              categoryName: wi.categoryName || "",
              description: undefined,
              displayOrder: undefined,
              isActive: true,
            },
          }));

        setWorkshopCostItems(items);
      } catch (error) {
        console.error("Atölye maliyet kalemleri yüklenemedi:", error);
        setWorkshopCostItems([]);
      } finally {
        setLoadingItems(false);
      }
    } else {
      setWorkshopCostItems([]);
    }
  };

  const handleCostItemChange = (costItemId: string) => {
    const item = workshopCostItems.find((i) => i.costItemId === costItemId);
    setSelectedCostItem(item || null);
    setFormError("");

    setFormData((prev) => ({
      ...prev,
      costItemId,
      unitPrice: item?.unitPrice || 0,
      currency: item?.currency || "TRY",
      unit: item?.unitName || item?.unit || "",
      unit2: item?.unit2 || "",
      quantity1Label: item?.quantity1Label,
      quantity2Label: item?.quantity2Label,
      calculationType: item?.calculationType,
    }));
  };

  const calculateTotal = () => {
    // CustomCost için totalCost direkt girilir, hesaplanmaz
    if (formData.calculationType === CalculationType.CustomCost) {
      return;
    }

    const qty1 = formData.quantity || 0;
    // Boya hesaplamada Çeşit Sayısı (quantity2) yalnız bilgi amaçlı — çarpana girmez
    const isPaintBased =
      formData.calculationType === CalculationType.PaintBased;
    const qty2 = isPaintBased ? 1 : formData.quantity2 || 1;
    const price = formData.unitPrice || 0;
    const wastage = formData.wastagePercentage || 0;

    let total = qty1 * qty2 * price;
    if (wastage > 0) {
      total = total * (1 + wastage / 100);
    }

    setFormData((prev) => ({ ...prev, totalCost: total }));
  };

  useEffect(() => {
    calculateTotal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.quantity,
    formData.quantity2,
    formData.unitPrice,
    formData.wastagePercentage,
  ]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedWorkshopId) {
      setFormError("Lütfen bir atölye seçin.");
      return;
    }

    if (!formData.costItemId) {
      setFormError("Lütfen bir maliyet kalemi seçin.");
      return;
    }

    // CustomCost için totalCost validasyonu
    if (formData.calculationType === CalculationType.CustomCost) {
      if (!formData.totalCost || formData.totalCost <= 0) {
        setFormError("Lütfen geçerli bir toplam tutar girin.");
        return;
      }
    } else {
      // Diğer tipler için quantity validasyonu
      if (!formData.quantity || formData.quantity <= 0) {
        setFormError("Lütfen geçerli bir miktar girin.");
        return;
      }
    }

    const { quantity1Label, quantity2Label, ...modelCostData } = formData;
    onSave(modelCostData);
  };

  const isCustom = formData.calculationType === CalculationType.CustomCost;
  const itemPlaceholder = loadingItems
    ? "Yükleniyor..."
    : !selectedWorkshopId
      ? "Önce atölye seçin"
      : workshopCostItems.length === 0
        ? "Bu atölyede tanımlı maliyet yok"
        : "Seçin...";

  return (
    <div className="po-overlay center top oc-sub" onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <form className="po-modal wide" role="dialog" aria-modal="true" aria-labelledby="oc-add-title" onSubmit={handleSubmit} noValidate>
        <div className="po-modal-head">
          <div>
            <span className="po-kicker">Sipariş maliyeti</span>
            <h3 id="oc-add-title">Yeni maliyet kalemi</h3>
          </div>
          <button type="button" className="po-ib big" aria-label="Kapat" onClick={onClose} disabled={saving}>{I.close}</button>
        </div>
        <div className="po-modal-body">
          <div className="po-form-sec">
            <h4>Kaynak</h4>
            <div className="po-grid2">
              {/* Atölye — önce seçilir, kalemler atölyeden gelir */}
              <label className="po-field">
                <span>Atölye <em className="err">zorunlu</em></span>
                <select ref={firstRef} value={selectedWorkshopId} onChange={(e) => handleWorkshopChange(e.target.value)} required>
                  <option value="">Önce atölye seçin...</option>
                  {workshops.map((workshop) => (
                    <option key={workshop.workshopId} value={workshop.workshopId}>
                      {workshop.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="po-field">
                <span>Maliyet kalemi <em className="err">zorunlu</em></span>
                <select
                  value={formData.costItemId || ""}
                  onChange={(e) => handleCostItemChange(e.target.value)}
                  required
                  disabled={!selectedWorkshopId || loadingItems}
                >
                  <option value="">{itemPlaceholder}</option>
                  {workshopCostItems.map((item) => (
                    <option key={item.costItemId} value={item.costItemId}>
                      {item.itemName} - {item.costCategory?.categoryName} (
                      {formatCurrency(item.unitPrice, item.currency)})
                    </option>
                  ))}
                </select>
                {selectedWorkshopId && workshopCostItems.length === 0 && !loadingItems && (
                  <span className="po-fieldnote warn">Bu atölyede henüz maliyet kalemi tanımlanmamış.</span>
                )}
                {formData.costItemId && (
                  <span className="po-fieldnote">
                    Birim fiyat {formatCurrency(formData.unitPrice || 0, formData.currency || "TRY")}
                    {isCustom ? " · özel maliyet: toplam tutar elle girilir" : ""}
                  </span>
                )}
              </label>
            </div>
          </div>

          <div className="po-form-sec">
            <h4>{isCustom ? "Tutar" : "Miktar"}</h4>
            <div className="po-grid2">
              {/* Miktar 1 - CustomCost hariç */}
              {!isCustom && (
                <label className="po-field">
                  <span>{formData.quantity1Label || "Miktar"} ({formData.unit || "birim"}) <em className="err">zorunlu</em></span>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.quantity || ""}
                    onChange={(e) => {
                      setFormError("");
                      setFormData((prev) => ({
                        ...prev,
                        quantity: parseFloat(e.target.value) || 0,
                      }));
                    }}
                    required
                    placeholder="Miktar girin"
                  />
                </label>
              )}

              {/* Miktar 2 */}
              {formData.quantity2Label && !isCustom && (
                <label className="po-field">
                  <span>{formData.quantity2Label} {formData.unit2 && `(${formData.unit2})`}</span>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.quantity2 || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        quantity2: parseFloat(e.target.value) || undefined,
                      }))
                    }
                    placeholder="Miktar girin"
                  />
                </label>
              )}

              {/* Toplam tutar — özel maliyette elle girilir */}
              {isCustom && (
                <label className="po-field">
                  <span>Toplam tutar ({formData.currency || "TRY"}) <em className="err">zorunlu</em></span>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.totalCost || ""}
                    onChange={(e) => {
                      setFormError("");
                      setFormData((prev) => ({
                        ...prev,
                        totalCost: parseFloat(e.target.value) || 0,
                      }));
                    }}
                    required
                    placeholder="Toplam tutar girin"
                  />
                </label>
              )}

              <label className="po-field span2">
                <span>Notlar <em>isteğe bağlı</em></span>
                <textarea
                  value={formData.notes || ""}
                  onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  placeholder="İsteğe bağlı notlar..."
                />
              </label>
            </div>
          </div>

          {formError && <div className="po-note err" role="alert">{formError}</div>}
        </div>
        <div className="po-modal-foot between">
          <div className="po-sum">
            <div className="grand">
              <span>Toplam tutar{isCustom ? "" : " · otomatik"}</span>
              <b>{formatCurrency(formData.totalCost || 0, formData.currency || "TRY")}</b>
            </div>
          </div>
          <div className="po-foot-btns">
            <button type="button" className="po-btn" onClick={onClose} disabled={saving}>Vazgeç</button>
            <button type="submit" className="po-btn primary" disabled={saving}>{saving ? "Kaydediliyor…" : "Kaydet"}</button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default OrderCostsModal;
